import * as typeguards from '../../typeguards/index';
import { FeatureSources, CodeType } from '../../enums/index';
import { filterAndAssembleArtifactList } from './artifact-filters';
import { completeSet } from './artifact-sets';

// as more types become linkable, we can add them here.
const linkableEvidenceTypes: Set<string> = new Set([
  FeatureSources.NOTE,
  FeatureSources.REASON_FOR_VISIT,
  FeatureSources.PROCEDURE_TYPE
  // FeatureSources.EXAM_TYPE,
  // FeatureSources.BODY_REGION,
  // FeatureSources.PROCEDURE_CATEGORY
]);

// ---------------- feature types  --------------------- //
const isSubhighlight = (
  feature: CMxAPI.Feature
): feature is CMxAPI.NgramFeature => {
  return typeguards.isNgram(feature) && `spans` in feature;
};

const isBlockHighlight = (
  feature: CMxAPI.Feature
): feature is CMxAPI.NgramFeature | CMxAPI.CategoricalFeature => {
  if (typeguards.isCategorical(feature)) {
    return true;
  }
  return typeguards.isNgram(feature) && !(`spans` in feature);
};

const getDescription = (feature: CMxAPI.Feature) => {
  if (typeguards.isNgram(feature)) {
    const found = (feature.spans ?? []).find(hasSentence);
    return found?.content ?? feature.ngram;
  }
  if (typeguards.isCategorical(feature)) {
    return feature.category;
  }
  return 'Unknown';
};

const hasSentence = (span: CMxAPI.Span) => span.level === 'sentence';

// ---------------- selection --------------------- //
const isSingleSelection = (
  selection: CMxArtifacts.FeatureSelection
): selection is CMxArtifacts.SingleSelection => {
  return `evidence` in selection && !Array.isArray(selection.evidence);
};

const isMultipleSelection = (
  selection: CMxArtifacts.FeatureSelection
): selection is CMxArtifacts.MultipleSelection => {
  return `evidence` in selection && Array.isArray(selection.evidence);
};

// ---------------- features  --------------------- //
const getFeature = (evidence: CMxAPI.UnionEvidence): any => {
  if (typeguards.isUnknownEvidence(evidence)) {
    return evidence.details;
  } else if (typeguards.isIndividualEvidence(evidence)) {
    return evidence.feature;
  } else {
    throw new Error('indeterminate evidence type');
  }
};

const getFeatureForSelection = (selection: CMxArtifacts.FeatureSelection) => {
  if (isSingleSelection(selection)) {
    return getFeature(selection.evidence);
  }
  throw new Error('feature selection not supported for multiples');
};

type DisplayableFeature = CMxAPI.NgramFeature | CMxAPI.CategoricalFeature;

const isDisplayedFeature = (
  feature: CMxAPI.Feature
): feature is DisplayableFeature =>
  typeguards.isNgram(feature) || typeguards.isCategorical(feature);

export const filterEvidence = (evidence: CMxAPI.UnionEvidence[]) => {
  // filter out priors
  const noPriors = evidence.filter(pieceOfEvidence => {
    return !typeguards.isPrior(pieceOfEvidence);
  });
  const onlyDisplayable = noPriors.filter(pieceOfEvidence => {
    const feature = getFeature(pieceOfEvidence);
    if (!isDisplayedFeature(feature)) {
      return false;
    } else if (typeguards.isCategorical(feature)) {
      return true;
    } else if (typeguards.isNgram(feature)) {
      return true;
    }
    return false;
  });
  return onlyDisplayable;
};

const buildEvidenceFeatures = (
  caseDetail: CMxAPI.CaseRun,
  artifactSet: Set<string> = completeSet
) => {
  // construct "Features" from raw evidence.
  const featureSelections: CMxArtifacts.MultipleSelection[] = [];
  const artifacts = filterAndAssembleArtifactList({
    caseRun: caseDetail,
    artifactSet
  });
  artifacts.forEach(artifact => {
    const {
      identifier: artifactId,
      procedures: { codes },
      diagnoses: { codes: standaloneDiagnosticCodes },
      modifiers: { codes: standaloneModifierCodes }
    } = artifact;

    const _generate = (codeType: string) => (code: CMxAPI.MedicalCode) => {
      if (code.evidence !== undefined) {
        const evidence = filterEvidence(code.evidence);

        if (evidence.length) {
          featureSelections.push({
            code,
            artifactId: artifactId as number | string,
            codeType,
            display: true,
            evidence
          });
        }
      }
    };

    const generateDiagnosis = _generate(CodeType.ICD);
    const generateModifier = _generate(CodeType.MODIFIER);
    const generateCPT = _generate(CodeType.CPT);

    codes.forEach(code => {
      generateCPT(code);
      code.diagnoses?.codes.forEach(generateDiagnosis);
      code.modifiers?.codes.forEach(generateModifier);
    });

    standaloneDiagnosticCodes.forEach(generateDiagnosis);
    standaloneModifierCodes.forEach(generateModifier);
  });

  return featureSelections;
};

export {
  isDisplayedFeature,
  linkableEvidenceTypes,
  isSubhighlight,
  isBlockHighlight,
  getDescription,
  hasSentence,
  isSingleSelection,
  isMultipleSelection,
  getFeature,
  getFeatureForSelection,
  buildEvidenceFeatures
};
